import React from 'react';
import {ArrToggle} from '../../../common/arr-toggle/ArrToggle';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {Box, Stack, Typography} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import '../ActionLikeDocuments.scss';
import {numberFormatter} from '../../../../utils/utils';
import {t} from 'i18next';

// Types Definitions
// ----------------------------------
interface IActionListItem {
  id: number | string;
  label: string;
  selected?: boolean;
  isLoading?: boolean;
  count: number;
}

interface ActionLikeProps {
  actionList: IActionListItem[];
  selected?: IActionListItem[];
  isLoading?: boolean;
  onActionChange?: (index: number, status: boolean) => void;
  errorMessage?: string;
  totalSelectedCount?: number;
}

// ----------------------------------
// ActionLike Component
// ----------------------------------
const ActionLike: React.FC<ActionLikeProps> = ({
  actionList,
  isLoading = false,
  onActionChange,
  errorMessage,
  totalSelectedCount,
}) => {
  const displayCount = totalSelectedCount ?? 0;
  const shouldShowLoader = isLoading || displayCount <= 0;

  return (
    <Box className="arr-action-like__wrapper" data-testid="action-like-wrapper">
      {/* ERROR MESSAGE */}
      {errorMessage && (
        <Box
          className="arr-action-like__error"
          role="alert"
          data-testid="action-like-error"
        >
          <ErrorOutlineIcon data-testid="action-like-erroroutline" />
          <Typography
            className="typo-text body3_size"
            data-testid="action-like-errormessage"
          >
            {errorMessage}
          </Typography>
        </Box>
      )}

      {/* ACTION LIST */}
      <Stack
        role="grid"
        direction="column"
        className="arr-action-like__content"
        data-testid="action-like-content"
      >
        {Boolean(actionList.length) &&
          actionList.map((action, index) => (
            <Box
              className="arr-action-like__action-item"
              data-testid="action-like-action-item"
              key={`${index}-${action.id}`}
              role="row"
            >
              <Box
                role="gridcell"
                className="action-item-labels"
                data-testid="action-like-action-item-labels"
              >
                <Typography
                  className="typo-text subhead5_size action-item-labels__count"
                  data-testid="action-like-item-label-count"
                  id={`count-${index}`}
                  aria-label={`${t('count')}:${action.count}`}
                >
                  {action.isLoading || shouldShowLoader ? (
                    <ArrLoadingSpinner
                      size={18}
                      aria-label={t('common.dataLoading')}
                    />
                  ) : (
                    action.count
                  )}
                </Typography>
                <Typography
                  id={`label-${index}`}
                  className="typo-text body3_size action-item-labels__label"
                  data-testid="action-like-item-label"
                >
                  {action.label}
                </Typography>
              </Box>

              <Box
                role="gridcell"
                className="action-item-action"
                data-testid="action-like-action"
              >
                <ArrToggle
                  value={action.selected}
                  size="medium"
                  onChange={(e) => onActionChange?.(index, e.target.checked)}
                  dataTestId="new-action-like-toggle"
                  slotProps={{
                    input: {
                      'aria-label': `${t('count')}:${action.count} ${action.label}`,
                      'aria-checked': action.selected,
                      role: 'switch',
                    },
                  }}
                />
              </Box>
            </Box>
          ))}
      </Stack>

      {/* TOTAL SELECTED */}
      <Box
        className="arr-action-like__selected"
        data-testid="action-like-selected"
        tabIndex={0}
      >
        <Typography
          className="typo-text subhead4_size arr-action-like__selected-count"
          data-testid="action-like-selected-count"
        >
          {shouldShowLoader ? (
            <ArrLoadingSpinner size={18} aria-label={t('common.dataLoading')} />
          ) : (
            numberFormatter.format(displayCount)
          )}
        </Typography>
        <div
          className="arr-action-like__selected-content"
          data-testid="action-like-selected-content"
        >
          <Typography
            className="typo-text subhead4_size title"
            data-testid="action-like-selected-label"
          >
            {t('actionMenu.actionLikeDocuments.selectedTitle')}
          </Typography>
          <Typography
            className="typo-text body4_size description"
            data-testid="action-like-selected-description"
          >
            {t('actionMenu.actionLikeDocuments.selectedDescription')}
          </Typography>
        </div>
      </Box>
    </Box>
  );
};

export {ActionLike};
export default ActionLike;
